import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function HeroSection() {
  return <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-401" data-mp="2hvm1w11ux880">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-402" data-mp="23wmfz15qb7fj">
        {/* Left Column: Copy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-2xl" data-id="element-403" data-mp="28w8v5g54axqz">
          <p className="text-sm font-medium text-teal mb-5" data-id="element-404" data-mp="2ms305o2rt2yt">
            SyNeural Frameworks
          </p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-[1.1] mb-4 text-balance" data-id="element-405" data-mp="3fil56un21eev">
            Human Capability Framework
          </h1>
          <p className="text-xl lg:text-2xl text-navy font-medium leading-snug mb-8 max-w-xl" data-id="element-406" data-mp="3kb4hloa0a3i3">
            Developing reasoning, judgment, and readiness for responsibility.
          </p>

          <p className="text-lg text-ink-muted mb-10 leading-relaxed max-w-xl" data-id="element-407" data-mp="2bbu2t6hdc42o">
            The Human Capability Framework brings together the Civic Judgment
            Index (CJI) and Neurocognitive Civic Judgment Index (NCJI) with
            methods for assessment, interpretation, and development. It
            connects findings with practical pathways for growth,
            reassessment, and readiness for responsibility.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-408" data-mp="384phvdnfmzlb">
            <a href="/cji" className="bg-navy hover:bg-navy-light text-white font-medium px-6 py-3.5 rounded-md transition-colors duration-150 shadow-sm flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-409" data-mp="381tlhtr8giyz">
              Explore CJI
              <ArrowRight size={18} data-id="element-410" data-mp="18swjbckzxm6h" />
            </a>
            <a href="/ncji" className="bg-transparent border border-navy text-navy hover:bg-surface-muted font-medium px-6 py-3.5 rounded-md transition-colors duration-150 flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-411" data-mp="38u4j0xyqp68r">
              Explore NCJI
              <ArrowRight size={18} data-id="element-412" data-mp="3mtclq0u42whp" />
            </a>
          </div>

          <p className="text-xs text-ink-muted font-medium" data-id="element-413" data-mp="3rs726w4lw8yn">
            For education, civil society, workforce systems, public
            institutions, and leadership development.
          </p>
        </motion.div>

        {/* Right Column: Abstract Diagram */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="relative h-[400px] lg:h-[500px] w-full flex items-center justify-center" data-id="element-414" data-mp="5d4gsdrirpsx">

          <svg viewBox="0 0 500 500" className="w-full h-full max-w-lg" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-415" data-mp="23vpt5g1sr6iy">
            {/* Background structural lines */}
            <circle cx="250" cy="250" r="200" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="4 8" data-id="element-416" data-mp="1pun3yufi9g3t" />
            <circle cx="250" cy="250" r="140" stroke="#D9E2EC" strokeWidth="1" data-id="element-417" data-mp="2ijat513dkpmn" />
            <circle cx="250" cy="250" r="80" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="2 4" data-id="element-418" data-mp="2dr189zh4afr8" />

            {/* Connecting Pathways */}
            <path d="M100 350 C 150 350, 200 250, 250 250" stroke="#245B8F" strokeWidth="1.5" className="opacity-60" data-id="element-419" data-mp="31i6o4en2581j" />
            <path d="M250 250 C 300 250, 350 150, 400 150" stroke="#2E8C88" strokeWidth="1.5" className="opacity-60" data-id="element-420" data-mp="2l50eluso3h08" />
            <path d="M150 150 C 200 200, 250 250, 300 350" stroke="#123B5D" strokeWidth="1.5" className="opacity-40" data-id="element-421" data-mp="2c42bdeq1zdip" />

            {/* Nodes & Labels */}
            {/* Individual */}
            <g transform="translate(100, 350)" data-id="element-422" data-mp="38si3nikjs0zi">
              <circle cx="0" cy="0" r="6" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-423" data-mp="38ucq51bgw15p" />
              <text x="-10" y="20" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-424" data-mp="2zxlirsrgszvt">
                Individual
              </text>
            </g>

            {/* Education */}
            <g transform="translate(150, 150)" data-id="element-425" data-mp="1ouxuu7nqrbv">
              <circle cx="0" cy="0" r="8" fill="#FFFFFF" stroke="#6FAE8A" strokeWidth="2" data-id="element-426" data-mp="1lyzj4rwe0mw" />
              <text x="0" y="-15" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-427" data-mp="aigc8cfwu6kr">
                Education
              </text>
            </g>

            {/* Workforce */}
            <g transform="translate(250, 250)" data-id="element-428" data-mp="3nuqfbh1slocs">
              <circle cx="0" cy="0" r="12" fill="#FFFFFF" stroke="#123B5D" strokeWidth="2.5" data-id="element-429" data-mp="3ntyoskn1ygtd" />
              <circle cx="0" cy="0" r="4" fill="#123B5D" data-id="element-430" data-mp="13754rn2wh3kw" />
              <text x="20" y="4" fontSize="12" fill="#1F2A37" fontFamily="Inter" fontWeight="600" data-id="element-431" data-mp="1b4p0mjmeyxm4">
                Workforce
              </text>
            </g>

            {/* Civil Society */}
            <g transform="translate(300, 350)" data-id="element-432" data-mp="1p46tpb6qsx1a">
              <circle cx="0" cy="0" r="10" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-433" data-mp="rhrc89ebfzdq" />
              <text x="0" y="22" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-434" data-mp="11fxcynodyzh0">
                Civil Society
              </text>
            </g>

            {/* Government */}
            <g transform="translate(350, 220)" data-id="element-435" data-mp="dp1p21n1wof1">
              <circle cx="0" cy="0" r="14" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-436" data-mp="66tqcbrnfa76" />
              <text x="22" y="4" fontSize="11" fill="#5B6675" fontFamily="Inter" fontWeight="500" data-id="element-437" data-mp="3se1p2v82ciu8">
                Government
              </text>
            </g>

            {/* Leadership */}
            <g transform="translate(400, 150)" data-id="element-438" data-mp="3emwysjql52wk">
              <circle cx="0" cy="0" r="18" fill="#FFFFFF" stroke="#123B5D" strokeWidth="3" data-id="element-439" data-mp="3el2d337hp6rf" />
              <circle cx="0" cy="0" r="26" stroke="#2E8C88" strokeWidth="1" strokeDasharray="2 2" data-id="element-440" data-mp="34q0pwcwryerh" />
              <text x="0" y="-32" fontSize="12" fill="#1F2A37" fontFamily="Inter" textAnchor="middle" fontWeight="600" data-id="element-441" data-mp="2vokmip4dncwr">
                Leadership
              </text>
            </g>
          </svg>
        </motion.div>
      </div>
    </section>;
}